import type {ErrorCode} from '@nursio-link/common';

import i18n from '@/i18n';

export const errorMessages: Record<ErrorCode, string> = {
  // Auth
  UNAUTHORIZED: i18n.t('errors.unauthorized'),
  INVALID_PASSWORD_OR_USERNAME: i18n.t('errors.invalidPasswordOrUsername'),
  FORBIDDEN: i18n.t('errors.forbidden'),
  INVALID_REQUEST: i18n.t('errors.invalidRequest'),
  MISSING_MODULE_PERMISSION: i18n.t('errors.missingModulePermission'),
  DEVICE_NOT_PAIRED: i18n.t('errors.deviceNotPaired'),
  DEVICE_PAIRING_REQUESTED: i18n.t('errors.devicePairingRequested'),
  DEVICE_NOT_FOUND: i18n.t('errors.deviceNotFound'),
  NEW_IP_FOR_DEVICE_REQUESTED: i18n.t('errors.newIpForRequested'),
  CANNOT_DELETE_LAST_DEVICE_IP: i18n.t('errors.cannotDeleteLastDeviceIp'),
  DEPLOY_CLIENT_ALREADY_EXISTS: i18n.t('errors.deployClientAlreadyExists'),

  // Notification
  EMAIL_ALREADY_SUBSCRIBED: i18n.t('errors.emailAlreadySubscribed'),
  SUBSCRIPTION_NOT_FOUND: i18n.t('errors.subscriptionNotFound'),
  EMAIL_NOT_VERIFIED: i18n.t('errors.emailNotVerified'),
  VERIFICATION_EMAIL_COOLDOWN: i18n.t('errors.verificationEmailCooldown'),
  VERIFICATION_TOKEN_NOT_FOUND: i18n.t('errors.verificationTokenNotFound'),
  VERIFICATION_TOKEN_EXPIRED: i18n.t('errors.verificationTokenExpired'),
  UNSUBSCRIBE_TOKEN_NOT_FOUND: i18n.t('errors.unsubscribeTokenNotFound'),
  MISSING_REQUIRED_ARGUMENTS: i18n.t('errors.missingRequiredArguments'),
  UNKNOWN_NOTIFICATION_TYPE: i18n.t('errors.unknownNotificationType'),

  // Task
  CANNOT_CREATE_TASK_FOR_NOT_ACTIVE_RESIDENT: i18n.t(
    'errors.cannotCreateTaskForNotActiveResident'
  ),
  START_DATE_MUST_BE_IN_THE_FUTURE: i18n.t('errors.startDateMustBeInTheFuture'),
  CANNOT_UNCOMPLETE_TASK: i18n.t('errors.cannotUncompleteTask'),
  TASK_ALREADY_COMPLETED: i18n.t('errors.taskAlreadyCompleted'),
  TASK_ALREADY_REJECTED: i18n.t('errors.taskAlreadyRejected'),
  TASK_NOT_FOUND: i18n.t('errors.taskNotFound'),
  TASK_MONTH_IS_ARCHIVED: i18n.t('errors.taskMonthIsArchived'),
  INVALID_ARCHIVE_MONTH: i18n.t('errors.invalidArchiveMonth'),

  // User
  USER_NOT_FOUND: i18n.t('errors.userNotFound'),
  PERSONAL_NUMBER_ALREADY_EXISTS: i18n.t('errors.personalNumberAlreadyExists'),
  COULD_NOT_CREATE_USER: i18n.t('errors.couldNotCreateUser'),
  COULD_NOT_UPDATE_USER: i18n.t('errors.couldNotUpdateUser'),
  INVALID_USER_GROUP: i18n.t('errors.invalidUserGroup'),
  USER_DOES_NOT_HAVE_ANY_GROUP_ASSIGNED: i18n.t(
    'errors.userDoesNotHaveAnyGroupAssigned'
  ),
  EMAIL_ALREADY_IN_USE: i18n.t('errors.emailAlreadyInUse'),
  COULD_NOT_RESET_PASSWORD: i18n.t('errors.couldNotResetPassword'),
  COULD_NOT_GENERATE_PASSWORD_FOR_THIS_GROUP: i18n.t(
    'errors.couldNotGeneratePasswordForThisGroup'
  ),

  // Resident
  RESIDENT_NOT_FOUND: i18n.t('errors.residentNotFound'),
  RESIDENT_NOT_OPERATIONALLY_ACTIVE: i18n.t(
    'errors.residentNotOperationallyActive'
  ),
  RESIDENT_NOT_DEACTIVATED: i18n.t('errors.residentNotDeactivated'),
  ACTIVE_RESIDENT_MUST_HAVE_ROOM_ASSIGNED: i18n.t(
    'errors.activeResidentMustHaveRoomAssigned'
  ),
  RESIDENT_ALREADY_ASSIGNED_TO_ROOM: i18n.t(
    'errors.residentAlreadyAssignedToRoom'
  ),
  COULD_NOT_DELETE_RESIDENT: i18n.t('errors.couldNotDeleteResident'),
  COULD_NOT_CREATE_RESIDENT: i18n.t('errors.couldNotCreateResident'),
  COULD_NOT_UPDATE_RESIDENT: i18n.t('errors.couldNotUpdateResident'),
  CONTACT_PERSON_NOT_MATCHING_RESIDENT: i18n.t(
    'errors.contactPersonNotMatchingResident'
  ),
  DUPLICATE_CONTACT_PERSON_EMAIL: i18n.t('errors.duplicateContactPersonEmail'),
  DUPLICATE_CONTACT_PERSON_PHONE_NUMBER: i18n.t(
    'errors.duplicateContactPersonPhoneNumber'
  ),
  ROOM_REQUIRED_FOR_ACCOMMODATION_DATE: i18n.t(
    'errors.roomRequiredForAccommodationDate'
  ),
  ROOM_NOT_ALLOWED_FOR_FUTURE_ACCOMMODATION_DATE: i18n.t(
    'errors.roomNotAllowedForFutureAccommodationDate'
  ),
  RESIDENT_HAS_ACTIVE_CONTACT_PERSON_ACCOUNT: i18n.t(
    'errors.residentHasActiveContactPersonAccount'
  ),
  CONTACT_PERSON_MUST_HAVE_PHONE_OR_EMAIL: i18n.t(
    'errors.contactPersonMustHavePhoneOrEmail'
  ),

  // Media
  FILE_NOT_FOUND: i18n.t('errors.fileNotFound'),
  FILE_ALREADY_EXISTS: i18n.t('errors.fileAlreadyExists'),
  NOT_ENOUGH_SPACE: i18n.t('errors.notEnoughSpace'),
  FILE_TOO_BIG: i18n.t('errors.fileTooBig'),
  AVATAR_NAME_NOT_MATCHING_ID: i18n.t('errors.avatarNameNotMatchingId'),

  // Inventory
  WAREHOUSE_ACCESS_FORBIDDEN: i18n.t('errors.warehouseAccessForbidden'),
  WAREHOUSE_NOT_FOUND: i18n.t('errors.warehouseNotFound'),
  ITEM_NOT_FOUND: i18n.t('errors.itemNotFound'),
  INSUFFICIENT_QUANTITY: i18n.t('errors.insufficientQuantity'),

  // Metric
  INVALID_METRIC_ID: i18n.t('errors.invalidMetricId'),

  // Medication
  INVALID_DAY_OF_THE_WEEK: i18n.t('errors.invalidDayOfTheWeek'),
  MEDICATION_NOT_FOUND: i18n.t('errors.medicationNotFound'),
  DOSAGE_DETAILS_ARE_REQUIRED: i18n.t('errors.dosageDetailsAreRequired'),
  INVALID_TIME_TYPE: i18n.t('errors.invalidTimeType'),
  TIME_MUST_BE_SPECIFIED_WHEN_USING_SPECIFIC_TIME_TYPE: i18n.t(
    'errors.timeMustBeSpecifiedWhenUsingSpecificTimeType'
  ),
  PART_OF_THE_DAY_MUST_BE_SPECIFIED_WHEN_USING_PART_OF_THE_DAY_TIME_TYPE:
    i18n.t('errors.partOfTheDayMustBeSpecifiedWhenUsingPartOfTheDayTimeType'),
  CANNOT_SPECIFY_BOTH_TIME_AND_PART_OF_THE_DAY: i18n.t(
    'errors.cannotSpecifyBothTimeAndPartOfTheDay'
  ),

  // Event
  EVENT_NOT_FOUND: i18n.t('errors.eventNotFound'),
  START_DATE_CANNOT_BE_AFTER_END_DATE: i18n.t(
    'errors.startDateCannotBeAfterEndDate'
  ),
  DATE_RANGE_EXCEEDS_ONE_YEAR: i18n.t('errors.dateRangeExceedsOneYear'),

  // Room
  ROOM_NOT_FOUND: i18n.t('errors.roomNotFound'),
  ROOM_ALREADY_EXISTS: i18n.t('errors.roomAlreadyExists'),
  ROOM_OCCUPIED: i18n.t('errors.roomOccupied'),
  ROOM_FULL: i18n.t('errors.roomFull'),
  COULD_NOT_DECREASE_ROOM_CAPACITY_BELOW_ACTUAL_RESIDENTS_AMOUNT: i18n.t(
    'errors.couldNotDecreaseRoomCapacityBelowActualResidentsAmount'
  ),

  // GraphQL
  COULD_NOT_FIND_MATCHING_RESOLVER: i18n.t(
    'errors.couldNotFindMatchingResolver'
  ),

  // Integration
  INTEGRATION_PROVIDER_NOT_FOUND: i18n.t('errors.integrationProviderNotFound'),
  INTEGRATION_PROVIDER_ALREADY_EXISTS: i18n.t(
    'errors.integrationProviderAlreadyExists'
  ),
  INTEGRATION_INVALID_API_KEY: i18n.t('errors.integrationInvalidApiKey'),
  INTEGRATION_PROVIDER_DISABLED: i18n.t('errors.integrationProviderDisabled'),
  INTEGRATION_ENDPOINT_NOT_ALLOWED: i18n.t(
    'errors.integrationEndpointNotAllowed'
  ),
  INTEGRATION_RATE_LIMIT_EXCEEDED: i18n.t(
    'errors.integrationRateLimitExceeded'
  ),

  // Health Log
  DATE_CANNOT_BE_IN_THE_FUTURE: i18n.t('errors.dateCannotBeInTheFuture'),

  // Work Time
  WORK_TIME_NOT_FOUND: i18n.t('errors.workTimeNotFound'),
  WORK_TIME_NOT_FINISHED: i18n.t('errors.workTimeNotFinished'),

  // Job Role
  JOB_ROLE_NOT_FOUND: i18n.t('errors.jobRoleNotFound'),
  JOB_ROLE_ALREADY_EXISTS: i18n.t('errors.jobRoleAlreadyExists'),
  JOB_ROLE_ASSIGNED_TO_USERS: i18n.t('errors.jobRoleAssignedToUsers'),
  JOB_ROLE_USED_IN_SHIFTS: i18n.t('errors.jobRoleUsedInShifts'),
  JOB_ROLE_USED_IN_STAFFING_REQUIREMENTS: i18n.t(
    'errors.jobRoleUsedInStaffingRequirements'
  ),
  JOB_ROLE_USED_IN_TASKS: i18n.t('errors.jobRoleUsedInTasks'),
  JOB_ROLE_USED_IN_RECURRING_TASKS: i18n.t(
    'errors.jobRoleUsedInRecurringTasks'
  ),

  // Work Schedule
  WORK_SCHEDULE_WEEK_ALREADY_PUBLISHED: i18n.t(
    'errors.workScheduleWeekAlreadyPublished'
  ),
  PLANNED_SHIFT_NOT_FOUND: i18n.t('errors.plannedShiftNotFound'),
  PLANNED_SHIFT_OVERLAP: i18n.t('errors.plannedShiftOverlap'),
  PLANNED_SHIFT_BLOCKED_BY_ABSENCE: i18n.t(
    'errors.plannedShiftBlockedByAbsence'
  ),

  // Absence Request
  ABSENCE_REQUEST_NOT_FOUND: i18n.t('errors.absenceRequestNotFound'),
  ABSENCE_REQUEST_ALREADY_PROCESSED: i18n.t(
    'errors.absenceRequestAlreadyProcessed'
  ),
  ABSENCE_REQUEST_CONFLICT: i18n.t('errors.absenceRequestConflict'),

  // General
  INTERNAL_SERVER_ERROR: i18n.t('errors.internalServerError'),
};
